import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { LANGUAGE_INFO, LANGUAGES, useAura } from "./AuraProvider";

// Simmi's language. She detects it from what the visitor writes or says, so this mostly just shows it; clicking
// opens a menu to pick one by hand. It opens upward (the dock sits at the bottom of the screen), or down from the
// chat panel's header (openDown).
const LanguageToggle = ({ className = "", openDown = false }) => {
  const { lang, chooseLanguage, t } = useAura();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !rootRef.current?.contains(e.target)) {
        e.stopPropagation(); // Esc closes the menu, not Simmi
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close, true);
    };
  }, [open]);

  const pick = (code) => {
    setOpen(false);
    chooseLanguage(code);
  };

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t.language}: ${LANGUAGE_INFO[lang].name}`}
        title={`${t.language}: ${LANGUAGE_INFO[lang].name}`}
        className={`flex h-9 min-w-9 items-center justify-center rounded-[11px] border border-white/10 bg-[#2a2a30] px-2 text-[12px] font-semibold text-white transition-colors hover:bg-[#34343b] ${className}`}
      >
        {LANGUAGE_INFO[lang].short}
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t.language}
          className={`aura-msg absolute right-0 z-10 ${openDown ? "top-full mt-2" : "bottom-full mb-2"} max-h-[min(60vh,340px)] w-44 overflow-y-auto rounded-[14px] border border-white/10 bg-[#141416] p-1 shadow-2xl`}
        >
          {LANGUAGES.map((code) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={code === lang}
                lang={code}
                onClick={() => pick(code)}
                className={`flex w-full items-center justify-between rounded-[10px] px-3 py-2 text-left text-[13px] transition-colors hover:bg-white/10 ${
                  code === lang ? "text-[#06D3DB]" : "text-white/85"
                }`}
              >
                {LANGUAGE_INFO[code].name}
                {code === lang && <Check className="h-3.5 w-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Chips under Simmi's "which language?" line, when she couldn't make out the visitor's speech.
export const LanguageChips = () => {
  const { lang, chooseLanguage } = useAura();
  return (
    <div className="flex flex-wrap gap-1.5">
      {LANGUAGES.map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          onClick={() => chooseLanguage(code)}
          className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
            code === lang
              ? "border-[#06D3DB] bg-[#06D3DB]/20 text-white"
              : "border-white/15 bg-white/[0.04] text-white/85 hover:border-[#06D3DB]/60 hover:text-white"
          }`}
        >
          {LANGUAGE_INFO[code].name}
        </button>
      ))}
    </div>
  );
};

export default LanguageToggle;
